// Documents library (workflow package C): the pure list logic behind
// pages/Documents.tsx — filter, sort, group, counts, vitals and row copy —
// kept DOM-free so it is unit-testable. The page fetches the whole pool once
// and everything below runs client-side.

import { documentsReturn, toolTarget } from './toolEmbed';
import { weekOf } from '../../shared/dates';
import { formatProjectNumber, formatWeekdayDate } from './calendar';
import type { PillTone, SortOption, SortValue } from '../components/primitives';
import type { Document, DocumentSourceState, DocumentStatus, DocumentTool, DocumentsResponse } from '../../shared/types';

export type DocumentsTab = 'all' | DocumentTool;

export const DOC_TABS = [
  { value: 'all', label: 'All' },
  { value: 'script', label: 'Scripts' },
  { value: 'callsheet', label: 'Call sheets' },
] as const satisfies readonly { value: DocumentsTab; label: string }[];

export const DOC_SORTS: SortOption[] = [
  { key: 'updated', label: 'Updated', natural: 'desc', naturalLabel: 'newest first' },
  { key: 'title', label: 'Title', natural: 'asc', naturalLabel: 'A–Z' },
  { key: 'client', label: 'Client', natural: 'asc', naturalLabel: 'A–Z' },
  { key: 'project', label: 'Project', natural: 'desc', naturalLabel: 'newest first' },
];

export const DEFAULT_DOC_SORT: SortValue = { key: 'updated', dir: 'desc' };

export const STATUS_LABEL: Record<DocumentStatus, string> = {
  approved: 'Approved',
  published: 'Published',
  in_review: 'In review',
  draft: 'Draft',
  archived: 'Archived',
};

export const STATUS_TONE: Record<DocumentStatus, PillTone> = {
  approved: 'accent',
  published: 'accent',
  in_review: 'gold',
  draft: undefined,
  archived: undefined,
};

/** Drafts and archived documents are governed by the "Include drafts"
    toggle, so the Status filter only offers the live states. */
export const STATUS_ITEMS = (['approved', 'in_review', 'published'] as const).map((s) => ({
  value: s,
  label: STATUS_LABEL[s],
}));

export interface DocumentsFilters {
  tab: DocumentsTab;
  query: string;
  client: string | null;
  /** OS project id. */
  project: string | null;
  status: string | null;
  /** "Include drafts" — also reveals archived documents. */
  drafts: boolean;
}

export function serializeDocSort(sort: SortValue): string {
  return `${sort.key}:${sort.dir}`;
}

export function parseDocSort(raw: string | undefined | null): SortValue | null {
  if (!raw) return null;
  const [key, dir] = raw.split(':');
  if (!key || !DOC_SORTS.some((o) => o.key === key)) return null;
  if (dir !== 'asc' && dir !== 'desc') return null;
  return { key, dir };
}

/** Hidden unless "Include drafts" is on. */
export function isHiddenByDefault(d: Document): boolean {
  return d.is_draft || d.is_archived || d.status === 'draft' || d.status === 'archived';
}

/** The status a row shows and filters on: the archived flag wins over
    whatever status the source reported. */
export function documentStatus(d: Document): DocumentStatus {
  return d.is_archived ? 'archived' : d.status;
}

/** "#0234 · Clinic welcome series" — the project label used by group heads
    and the project picker. */
export function projectLabel(number: number | null, name: string | null): string {
  const num = formatProjectNumber(number);
  const label = name || 'Untitled project';
  return num ? `${num} · ${label}` : label;
}

export function matchesDocument(d: Document, f: DocumentsFilters): boolean {
  if (!f.drafts && isHiddenByDefault(d)) return false;
  if (f.tab !== 'all' && d.tool !== f.tab) return false;
  if (f.client && d.client_name !== f.client) return false;
  if (f.project && d.project_id !== f.project) return false;
  if (f.status && documentStatus(d) !== f.status) return false;
  const q = f.query.trim().toLowerCase();
  if (q) {
    const hay = [d.title, d.client_name ?? '', formatProjectNumber(d.project_number) ?? '', d.project_name ?? '']
      .join(' ')
      .toLowerCase();
    if (!hay.includes(q)) return false;
  }
  return true;
}

function sortKeyOf(d: Document, key: string): string | number | null {
  switch (key) {
    case 'title': return d.title || null;
    case 'client': return d.client_name || null;
    case 'project': return d.project_number;
    default: return d.updated_at;
  }
}

/** Missing values (no client, unlinked) always sink whichever way the arrow
    points; ties fall back to most recently updated first. */
export function compareDocuments(sort: SortValue) {
  const flip = sort.dir === 'asc' ? 1 : -1;
  const text = sort.key === 'title' || sort.key === 'client';
  return (a: Document, b: Document) => {
    const ka = sortKeyOf(a, sort.key);
    const kb = sortKeyOf(b, sort.key);
    if (ka === null || kb === null) {
      if (ka !== null) return -1;
      if (kb !== null) return 1;
    } else {
      const cmp = text
        ? String(ka).localeCompare(String(kb), undefined, { sensitivity: 'base' })
        : ka < kb ? -1 : ka > kb ? 1 : 0;
      if (cmp !== 0) return cmp * flip;
    }
    return b.updated_at - a.updated_at;
  };
}

export function visibleDocuments(docs: readonly Document[], f: DocumentsFilters, sort: SortValue): Document[] {
  return docs.filter((d) => matchesDocument(d, f)).sort(compareDocuments(sort));
}

export interface DocumentGroup {
  key: string;
  /** null for the Unlinked group. */
  projectId: string | null;
  title: string;
  clientName: string | null;
  docs: Document[];
}

/** Group visible documents by project, most recent activity first; the
    Unlinked group always sits last. Rows keep the incoming (sorted) order. */
export function groupDocuments(visible: readonly Document[]): DocumentGroup[] {
  const byKey = new Map<string, DocumentGroup & { latest: number }>();
  for (const d of visible) {
    const key = d.project_id ?? 'unlinked';
    let g = byKey.get(key);
    if (!g) {
      g = {
        key,
        projectId: d.project_id,
        title: d.project_id ? projectLabel(d.project_number, d.project_name) : 'Unlinked',
        clientName: d.project_id ? d.client_name : null,
        docs: [],
        latest: 0,
      };
      byKey.set(key, g);
    }
    g.docs.push(d);
    g.latest = Math.max(g.latest, d.updated_at);
  }
  return [...byKey.values()]
    .sort((a, b) => {
      if (a.key === 'unlinked') return 1;
      if (b.key === 'unlinked') return -1;
      return b.latest - a.latest;
    })
    .map(({ latest: _latest, ...g }) => g);
}

export interface DocumentsCounts {
  all: number;
  script: number;
  callsheet: number;
}

/** Tab counts over the whole pool — they follow the drafts toggle only. */
export function documentCounts(docs: readonly Document[], drafts: boolean): DocumentsCounts {
  const shown = docs.filter((d) => drafts || !isHiddenByDefault(d));
  return {
    all: shown.length,
    script: shown.filter((d) => d.tool === 'script').length,
    callsheet: shown.filter((d) => d.tool === 'callsheet').length,
  };
}

export interface DocumentsVitals {
  inReview: number;
  updatedThisWeek: number;
}

/** Both vitals follow the drafts toggle like the rows and tab counts do;
    "Updated this week" is the Mon–Sun Melbourne week containing `today`. */
export function documentVitals(docs: readonly Document[], today: string, drafts: boolean): DocumentsVitals {
  const week = weekOf(today);
  const shown = docs.filter((d) => drafts || !isHiddenByDefault(d));
  return {
    inReview: shown.filter((d) => documentStatus(d) === 'in_review').length,
    updatedThisWeek: shown.filter((d) => d.updated_date >= week.start && d.updated_date <= week.end).length,
  };
}

/** "Script · v3", "Call sheet · Thu 9 Oct", "Client schedule · …";
    unlinked rows append the client so the row still says whose it is. */
export function documentMetaLine(d: Document): string {
  let line: string;
  if (d.tool === 'script') {
    line = `Script · v${d.version}`;
  } else {
    line = d.doc_type === 'client_schedule' ? 'Client schedule' : 'Call sheet';
    if (d.shoot_date) line += ` · ${formatWeekdayDate(d.shoot_date)}`;
  }
  if (!d.project_id && d.client_name) line += ` · ${d.client_name}`;
  return line;
}

export type DocumentView = 'editor' | 'client' | 'full' | 'shoot';

export function documentViews(d: Document): { value: DocumentView; label: string }[] {
  return [
    { value: 'editor', label: d.tool === 'script' ? 'Editor' : 'Call-sheet workspace' },
    { value: d.tool === 'script' ? 'client' : 'full', label: d.tool === 'script' ? 'Client review' : 'Full call sheet' },
    { value: 'shoot', label: 'Shoot view' },
  ];
}

/** Safe ToolEmbed entry, or null when the source has no registered admin URL. */
export function documentHref(d: Document, view?: DocumentView, returnTo?: string): string | null {
  const tool = d.tool === 'script' ? 'scripts' : 'callsheets';
  const url = toolTarget(tool, d.admin_url);
  if (!url) return null;
  if (view) url.searchParams.set('osView', documentViews(d).some((v) => v.value === view) ? view : 'editor');
  const params = new URLSearchParams({ url: url.href });
  const safeReturn = documentsReturn(returnTo ?? null);
  if (safeReturn) params.set('returnTo', safeReturn);
  return `/tools/${tool}?${params}`;
}

export function clientItems(docs: readonly Document[]): { value: string; label: string }[] {
  const names = new Set<string>();
  for (const d of docs) if (d.client_name) names.add(d.client_name);
  return [...names].sort((a, b) => a.localeCompare(b)).map((v) => ({ value: v, label: v }));
}

export function projectItems(
  projects: DocumentsResponse['projects'],
): { value: string; label: string; sub?: string }[] {
  return projects.map((p) => ({
    value: p.id,
    label: projectLabel(p.project_number, p.name),
    ...(p.client_name ? { sub: p.client_name } : {}),
  }));
}

/** Notice copy for a failed source, or null when both answered. A source
    that failed after some of its pages arrived is "partly loaded", not
    unreachable. */
export function outageMessage(
  sources: { scripts: DocumentSourceState; callsheets: DocumentSourceState },
  documents: readonly Document[] = [],
): string | null {
  const scriptsDown = sources.scripts.ok === false;
  const callsheetsDown = sources.callsheets.ok === false;
  const has = (tool: DocumentTool) => documents.some((d) => d.tool === tool);
  if (scriptsDown && callsheetsDown) {
    return has('script') || has('callsheet')
      ? 'Documents couldn’t be fully loaded — some may be missing.'
      : 'Documents couldn’t be reached.';
  }
  if (callsheetsDown) {
    return has('callsheet')
      ? 'Call sheets couldn’t be fully loaded — some may be missing.'
      : 'Call sheets couldn’t be reached — showing scripts only.';
  }
  if (scriptsDown) {
    return has('script')
      ? 'Scripts couldn’t be fully loaded — some may be missing.'
      : 'Scripts couldn’t be reached — showing call sheets only.';
  }
  return null;
}

// Last response, so returning to the page paints at once while the fresh
// fetch runs. Per tab and secret-free; dropped on session expiry.
let lastResponse: DocumentsResponse | null = null;

export function cachedDocuments(): DocumentsResponse | null {
  return lastResponse;
}

export function rememberDocuments(r: DocumentsResponse | null): void {
  lastResponse = r;
}
